import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, EllipsisVerticalIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../ui/cn';

const PreviewComposer = ({ placeholder, primaryColor, multiline = false }) => (
  <div className="border-t border-gray-200 bg-white px-3 py-3">
    <div className={cn('flex gap-2', multiline ? 'items-end' : 'items-center')}>
      {multiline ? (
        <textarea
          placeholder={placeholder}
          disabled
          rows={1}
          className="min-h-[44px] max-h-[120px] flex-1 resize-none rounded-2xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-snug text-gray-900 outline-none"
        />
      ) : (
        <input
          type="text"
          placeholder={placeholder}
          disabled
          className="h-11 flex-1 min-w-0 rounded-full border border-gray-200 bg-gray-50 px-4 text-sm text-gray-900 outline-none"
        />
      )}
      <button
        type="button"
        disabled
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white shadow-sm disabled:cursor-default"
        style={{ backgroundColor: primaryColor }}
      >
        <PaperAirplaneIcon className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const WidgetPreview = ({ widgetConfig, externalPreviewMode, onPreviewModeChange }) => {
  const { t } = useTranslation();
  const [previewMode, setPreviewMode] = useState('intro');
  
  // Sync with external control if provided
  useEffect(() => {
    if (externalPreviewMode) {
      setPreviewMode(externalPreviewMode);
    }
  }, [externalPreviewMode]);
  
  // Notify parent when mode changes
  const handlePreviewModeChange = (mode) => {
    setPreviewMode(mode);
    if (onPreviewModeChange) {
      onPreviewModeChange(mode);
    }
  };

  // Sample conversation for preview
  const sampleMessages = [
    { sender: 'user', content: "I've been looking for this model for ages!" },
    { sender: 'agent', content: "Let me check that for you real quick! We've just had a big delivery in the morning. All the products will be back in stock tomorrow but I can send a pair to you today! 🔥" },
    { sender: 'user', content: "That's great! 😃" },
    { sender: 'user', content: "Thank you very much!" }
  ];

  // Sample FAQ items
  const sampleFaqItems = widgetConfig.widget_questions?.slice(0, 3) || [
    { text: "What is Prose Captain?" },
    { text: "Discover Prose Captain Premium" },
    { text: "How can I log into my account?" }
  ];

  const getPrimaryColor = () => widgetConfig.widget_color || '#3b82f6';

  const previewModes = [
    { value: 'intro', label: t('widgets.config.preview.intro') },
    { value: 'conversation', label: t('widgets.config.preview.conversation') }
  ];

  const questionPlaceholder = widgetConfig.widget_language === 'pl'
    ? 'Wpisz swoje pytanie'
    : 'Type your question';

  return (
    <div className="sticky top-4">
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        {/* Preview Header */}
        <div className="flex items-center justify-center mb-4">
          <div className="inline-flex rounded-full bg-gray-100 p-1" role="group" aria-label={t('widgets.config.preview.title')}>
            {previewModes.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={previewMode === value}
                onClick={() => handlePreviewModeChange(value)}
                className={cn(
                  'px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap',
                  previewMode === value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Widget Preview Container */}
        <div className="relative bg-gray-50 rounded-lg p-4 overflow-hidden" style={{ minHeight: '600px' }}>
          {/* Simulated page background */}
          <div className="absolute inset-0 rounded-lg overflow-hidden" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #f0f0f0, #f0f0f0 10px, #ffffff 10px, #ffffff 20px)',
            opacity: 0.3
          }}></div>

          {/* Widget */}
          <div 
            className={`relative ${widgetConfig.widget_position === 'left' ? 'mr-auto' : widgetConfig.widget_position === 'center' ? 'mx-auto' : 'ml-auto'} bg-white overflow-hidden flex flex-col w-full max-w-[400px]`}
            style={{ 
              maxHeight: '550px',
              height: '550px',
              borderRadius: '20px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
          >
            {previewMode === 'intro' ? (
              /* INTRO MODE - Welcome Screen */
              <>
                {/* Welcome Header */}
                <div
                  style={{
                    background: '#ffffff',
                    color: '#1f2937',
                    padding: '16px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{ flex: 1, paddingRight: '8px' }}>
                    <h1 style={{ 
                      fontSize: '20px',
                      margin: '4px 0 6px 0',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      fontFamily: 'sans-serif'
                    }}>
                      {widgetConfig.widget_title || 'Hi there! 👋'}
                    </h1>
                    <p style={{ 
                      fontSize: '14px',
                      margin: 0,
                      lineHeight: 1.45,
                      fontWeight: 400,
                      color: '#6b7280'
                    }}>
                      {widgetConfig.widget_subtitle || 'Welcome to Prose Captain Support! We\'re ready to help you 😊'}
                    </p>
                  </div>
                  <button 
                    style={{
                      backgroundColor: 'transparent',
                      borderRadius: '4px',
                      border: 'none',
                      padding: '5px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '24px',
                      flexShrink: 0
                    }}
                  >
                    <XMarkIcon className="w-5 h-5" style={{ color: '#6b7280' }} />
                  </button>
                </div>

                {/* FAQ Items */}
                {sampleFaqItems.length > 0 && (
                  <div style={{ 
                    flex: 1,
                    overflowY: 'auto',
                    padding: '12px',
                    background: 'white'
                  }}>
                    {sampleFaqItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: '#f3f4f6',
                          borderRadius: '8px',
                          padding: '12px 14px',
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      >
                        <span style={{ 
                          fontSize: '14px',
                          color: '#1f2937',
                          flex: 1
                        }}>
                          {item.text || item.question}
                        </span>
                        <span 
                          style={{ 
                            fontSize: '20px',
                            color: '#6b7280',
                            transition: 'transform 0.3s'
                          }}
                        >
                          ›
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <PreviewComposer
                  placeholder={questionPlaceholder}
                  primaryColor={getPrimaryColor()}
                />
              </>
            ) : (
              /* CONVERSATION MODE - Chat Screen */
              <>
                {/* Chat Header */}
                <div
                  style={{
                    background: '#ffffff',
                    color: '#1f2937',
                    fontSize: '16px',
                    fontWeight: 500,
                    minHeight: '64px',
                    padding: '0 16px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontFamily: 'sans-serif'
                  }}
                >
                  <span aria-hidden="true" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button style={{ 
                      padding: '4px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}>
                      <ChevronDownIcon className="w-5 h-5" />
                    </button>
                    <button style={{ 
                      padding: '4px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}>
                      <EllipsisVerticalIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Chat Messages */}
                <div style={{ 
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '16px',
                  paddingRight: '8px', // Reduce right padding to account for scrollbar
                  background: '#f9fafb',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {/* Welcome message as first message from agent */}
                  {widgetConfig.welcome_message && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{
                        padding: '12px 16px',
                        lineHeight: 1.45,
                        borderRadius: '18px 18px 18px 4px',
                        backgroundColor: '#ffffff',
                        color: '#111827',
                        maxWidth: '80%',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                        fontSize: '14px'
                      }}>
                        {widgetConfig.welcome_message}
                      </div>
                    </div>
                  )}
                  {sampleMessages.map((message, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start', marginBottom: '12px' }}>
                      <div
                        style={{
                          padding: '12px 16px',
                          lineHeight: 1.45,
                          borderRadius: message.sender === 'user' 
                            ? '18px 18px 4px 18px' 
                            : '18px 18px 18px 4px',
                          color: message.sender === 'user' ? 'white' : '#111827',
                          backgroundColor: message.sender === 'user' 
                            ? getPrimaryColor() 
                            : '#ffffff',
                          maxWidth: '80%',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
                          fontSize: '14px'
                        }}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}
                </div>

                <PreviewComposer
                  placeholder={t('widgets.config.preview.typeMessage')}
                  primaryColor={getPrimaryColor()}
                  multiline
                />

                {/* AI Disclaimer Footer */}
                {widgetConfig.widget_link && widgetConfig.widget_link.trim() !== '' && (
                  <div style={{
                    fontSize: '11px',
                    color: '#666',
                    textAlign: 'center',
                    padding: '10px 12px',
                    borderTop: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}>
                    <span>
                      {widgetConfig.widget_language === 'pl' ? 'Masz dodatkowe pytania?' : 'Have additional questions?'}
                    </span>
                    <a
                      href={widgetConfig.widget_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#666',
                        textDecoration: 'underline'
                      }}
                    >
                      {widgetConfig.widget_language === 'pl' ? 'Skontaktuj się z nami' : 'Contact us'}
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Preview Footer */}
        <p className="text-xs text-gray-500 mt-4 text-center">
          {t('widgets.config.preview.livePreviewHint')}
        </p>
      </div>
    </div>
  );
};

export default WidgetPreview;
